import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import getIdentifier from '../../../getIdentifier';
import AccountPickerContext from '../../../contexts/AccountPickerContext';
import STORE from '../../../store';
import isDefined from '../../../isDefined';
import _ from 'lodash';

export const LabelsContext = React.createContext<{
  labels: FleetOps.Label[];
  labelLookup: { [id: string]: FleetOps.Label | undefined };
  refreshLabels: () => void;
}>({
  labels: [],
  labelLookup: {},
  refreshLabels: () => {},
});

export const useLabels = (labelIds: string[]) => {
  const { labels: allLabels } = useContext(LabelsContext);

  const labels = useMemo(() => {
    return _.sortBy(
      _.uniq(labelIds)
        .map((labelId) => {
          return allLabels.find((l) => l.id === labelId);
        })
        .filter(isDefined),
      'name',
    );
  }, [allLabels, labelIds]);

  return labels;
};

const LabelsProvider = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [labels, setLabels] = useState<FleetOps.Label[]>([]);
  const [refreshKey, setRefreshKey] = useState<string>(getIdentifier());
  const refreshLabels = useCallback(() => {
    setRefreshKey(getIdentifier());
  }, []);

  const labelLookup = useMemo((): {
    [id: string]: FleetOps.Label | undefined;
  } => {
    const lookup: { [id: string]: FleetOps.Label | undefined } = {};
    labels.forEach((label) => {
      lookup[label.id] = label;
    });
    return lookup;
  }, [labels]);

  useEffect(() => {
    STORE.getLabelsRef({
      accountId: selectedAccountId,
    })
      .get()
      .then((snapshot) => {
        const newLabels: FleetOps.Label[] = [];
        snapshot.forEach((snapshot) => {
          newLabels.push(snapshot.data());
        });
        setLabels(newLabels);
      });
  }, [refreshKey, selectedAccountId]);

  return (
    <LabelsContext.Provider value={{ labels, refreshLabels, labelLookup }}>
      {children}
    </LabelsContext.Provider>
  );
};

export default LabelsProvider;
